import {ActionCreatorDefinition, createAction} from "redux-ts";
import {TranslationUnit} from "platform/translation/TranslationUnit";
import {
    ChangePasswordFormState,
    ForgotPasswordFormState,
    LoginFormState, ResetPasswordFormState,
    SignUpFormState,
    TcAgreementState
} from "core/redux/login/LoginReduxState";
import {SignUpCountry} from "protocol/auth/GetSignUpCountriesResponse";
import {ContactInfoSource} from "enum/ContactInfoSource";
import {RouteType} from "core/router/Routes";
import {SignUpErrorCode} from "protocol/auth/SignUpResponse";
import {MobilePlatformType} from "protocol/auth/LoginRequest";
import {VerificationMethod} from "protocol/auth/VerificationMethod";
import {VerificationMethodType} from "enum/VerificationMethodType";

export const SetLoginRouteType: string = "SetLoginRoute";
export const SaveSignUpFormType: string = "SaveSignUpForm";
export const SaveLoginFormType: string = "SaveLoginForm";
export const SaveForgotPasswordFormType: string = "SaveForgotPasswordForm";
export const SaveResetPasswordFormType: string = "SaveResetPasswordForm";
export const SaveChangePasswordFormType: string = "SaveChangePasswordForm";
export const DoSubmitSignUpFormType: string =  "DoSubmitSignUpForm";
export const DoSubmitLoginFormType: string =  "DoSubmitLoginForm";
export const DoSubmitForgotPasswordFormType: string =  "DoSubmitForgotPasswordForm";
export const DoSubmitResetPasswordFormType: string =  "DoSubmitResetPasswordForm";
export const DoSubmitChangePasswordFormType: string =  "DoSubmitChangePasswordForm";
export const DoLoginNotifyUserAgreementType: string =  "DoLoginNotifyUserAgreement";
export const FetchSignUpCountriesType: string =  "FetchSignUpCountries";
export const SetSignUpCountriesType: string =  "SetSignUpCountries";
export const SetSignUpErrorType: string =  "SetSignUpError";
export const SetLoginErrorType: string =  "SetLoginError";
export const SetForgotPasswordErrorType: string =  "SetForgotPasswordError";
export const SetVerificationCodeErrorType: string =  "SetVerificationCodeError";
export const SetResetPasswordErrorType: string =  "SetResetPasswordError";
export const SetChangePasswordErrorType: string =  "SetChangePasswordError";
export const SetChangePasswordRequiredType: string =  "SetChangePasswordRequired";
export const SetTCLoginAgreementType: string =  "SetTCLoginAgreement";
export const GetResetPasswordVerificationCodeType: string =  "GetResetPasswordVerificationCode";
export const DoVerifyResetPasswordType: string =  "DoVerifyResetPassword";
export const SetT2FaVerificationEnabledType: string =  "SetT2FaVerificationEnabled"
export const SetVerificationMethodsType: string = "SetVerificationMethods";
export const SetVerificationMethodType: string = "SetVerificationMethod";
export const SetVerificationCodeIssuedType: string = "SetVerificationCodeIssued";
export const SetVerificationIsLimitReachedType: string = "SetVerificationIsLimitReached";

export interface SetLoginRoutePayload {

    route: RouteType;
}

export interface SaveSignUpFormPayload {

    form: SignUpFormState;
}

export interface SaveLoginFormPayload {

    form: LoginFormState;
}

export interface SaveForgotPasswordFormPayload {

    form: ForgotPasswordFormState;
}

export interface SaveResetPasswordFormPayload {

    form: ResetPasswordFormState;
}

export interface SaveChangePasswordFormPayload {

    form: ChangePasswordFormState;
}

export interface DoSubmitSignUpFormPayload {

    form: SignUpFormState;
    signUpSource: ContactInfoSource;
    action: (routeType: RouteType) => void;
}

export interface DoSubmitLoginFormPayload {

    form: LoginFormState;
    mobilePlatformType: MobilePlatformType;
    action: () => void;
}

export interface DoLoginNotifyUserAgreementPayload {

    mobilePlatformType: MobilePlatformType;
}

export interface DoSubmitForgotPasswordFormPayload {

    form: ForgotPasswordFormState;
    verificationMethodType: VerificationMethodType
    action: () => void;
}

export interface DoSubmitResetPasswordFormPayload {

    form: ResetPasswordFormState;
    mobilePlatformType: MobilePlatformType;
    action: () => void;
}

export interface DoSubmitChangePasswordFormPayload {

    form: ChangePasswordFormState;
    mobilePlatformType: MobilePlatformType;
    action: () => void;
}

export interface SetSignUpCountriesPayload {

    countryCodeByIP: string;
    countries: SignUpCountry[];
}

export interface SetSignUpErrorPayload {

    error: {
        errorCode?: SignUpErrorCode;
        trUnit?: TranslationUnit;
    }
}

export interface SetLoginErrorPayload {

    error: TranslationUnit;
}

export interface SetChangePasswordRequiredPayload {

    required: boolean;
}

export interface SetTCLoginAgreementPayload {

    tcAgreement: TcAgreementState;
}

export interface DoVerifyResetPasswordPayload {

    code: string;
    action: () => void;
}

export interface SetT2FaVerificationEnabledPayload {

    enable2FVerification: boolean;
}

export interface SetVerificationMethodsPayload {

    methods: VerificationMethod[];
}

export interface VerificationMethodPayload {

    method: VerificationMethodType;
    ResendVerificationCode?: boolean;
}

export interface SetVerificationCodeIssuedPayload {

    issued: boolean;
}

export interface SetVerificationIsLimitReachedPayload {

    IsLimitReached: boolean;
}

export const SetLoginRoute: ActionCreatorDefinition<SetLoginRoutePayload, any> = createAction<SetLoginRoutePayload>(SetLoginRouteType);
export const SaveSignUpForm: ActionCreatorDefinition<SaveSignUpFormPayload, any> = createAction<SaveSignUpFormPayload>(SaveSignUpFormType);
export const SaveLoginForm: ActionCreatorDefinition<SaveLoginFormPayload, any> = createAction<SaveLoginFormPayload>(SaveLoginFormType);
export const SaveForgotPasswordForm: ActionCreatorDefinition<SaveForgotPasswordFormPayload, any> = createAction<SaveForgotPasswordFormPayload>(SaveForgotPasswordFormType);
export const SaveResetPasswordForm: ActionCreatorDefinition<SaveResetPasswordFormPayload, any> = createAction<SaveResetPasswordFormPayload>(SaveResetPasswordFormType);
export const SaveChangePasswordForm: ActionCreatorDefinition<SaveChangePasswordFormPayload, any> = createAction<SaveChangePasswordFormPayload>(SaveChangePasswordFormType);
export const DoSubmitSignUpForm: ActionCreatorDefinition<DoSubmitSignUpFormPayload, any> = createAction<DoSubmitSignUpFormPayload>(DoSubmitSignUpFormType);
export const DoSubmitLoginForm: ActionCreatorDefinition<DoSubmitLoginFormPayload, any> = createAction<DoSubmitLoginFormPayload>(DoSubmitLoginFormType);
export const DoSubmitForgotPasswordForm: ActionCreatorDefinition<DoSubmitForgotPasswordFormPayload, any> = createAction<DoSubmitForgotPasswordFormPayload>(DoSubmitForgotPasswordFormType);
export const DoSubmitResetPasswordForm: ActionCreatorDefinition<DoSubmitResetPasswordFormPayload, any> = createAction<DoSubmitResetPasswordFormPayload>(DoSubmitResetPasswordFormType);
export const DoSubmitChangePasswordForm: ActionCreatorDefinition<DoSubmitChangePasswordFormPayload, any> = createAction<DoSubmitChangePasswordFormPayload>(DoSubmitChangePasswordFormType);
export const DoLoginNotifyUserAgreement: ActionCreatorDefinition<DoLoginNotifyUserAgreementPayload, any> = createAction<DoLoginNotifyUserAgreementPayload>(DoLoginNotifyUserAgreementType);
export const FetchSignUpCountries: ActionCreatorDefinition<any, any> = createAction<any>(FetchSignUpCountriesType);
export const SetSignUpCountries: ActionCreatorDefinition<SetSignUpCountriesPayload, any> = createAction<SetSignUpCountriesPayload>(SetSignUpCountriesType);
export const SetSignUpError: ActionCreatorDefinition<SetSignUpErrorPayload, any> = createAction<SetSignUpErrorPayload>(SetSignUpErrorType);
export const SetLoginError: ActionCreatorDefinition<SetLoginErrorPayload, any> = createAction<SetLoginErrorPayload>(SetLoginErrorType);
export const SetForgotPasswordError: ActionCreatorDefinition<SetLoginErrorPayload, any> = createAction<SetLoginErrorPayload>(SetForgotPasswordErrorType);
export const SetVerificationCodeError: ActionCreatorDefinition<SetLoginErrorPayload, any> = createAction<SetLoginErrorPayload>(SetVerificationCodeErrorType);
export const SetResetPasswordError: ActionCreatorDefinition<SetLoginErrorPayload, any> = createAction<SetLoginErrorPayload>(SetResetPasswordErrorType);
export const SetChangePasswordError: ActionCreatorDefinition<SetLoginErrorPayload, any> = createAction<SetLoginErrorPayload>(SetChangePasswordErrorType);
export const SetChangePasswordRequired: ActionCreatorDefinition<SetChangePasswordRequiredPayload, any> = createAction<SetChangePasswordRequiredPayload>(SetChangePasswordRequiredType);
export const SetTCLoginAgreement: ActionCreatorDefinition<SetTCLoginAgreementPayload, any> = createAction<SetTCLoginAgreementPayload>(SetTCLoginAgreementType);
export const GetResetPasswordVerificationCode: ActionCreatorDefinition<VerificationMethodPayload, any> = createAction<VerificationMethodPayload>(GetResetPasswordVerificationCodeType);
export const DoVerifyResetPassword: ActionCreatorDefinition<DoVerifyResetPasswordPayload, any> = createAction<DoVerifyResetPasswordPayload>(DoVerifyResetPasswordType);
export const SetT2FaVerificationEnabled: ActionCreatorDefinition<SetT2FaVerificationEnabledPayload, any> = createAction<SetT2FaVerificationEnabledPayload>(SetT2FaVerificationEnabledType);
export const SetVerificationMethods: ActionCreatorDefinition<SetVerificationMethodsPayload, any> = createAction<SetVerificationMethodsPayload>(SetVerificationMethodsType);
export const SetVerificationMethod: ActionCreatorDefinition<VerificationMethodPayload, any> = createAction<VerificationMethodPayload>(SetVerificationMethodType);
export const SetVerificationCodeIssued: ActionCreatorDefinition<SetVerificationCodeIssuedPayload, any> = createAction<SetVerificationCodeIssuedPayload>(SetVerificationCodeIssuedType);
export const SetVerificationIsLimitReached: ActionCreatorDefinition<SetVerificationIsLimitReachedPayload, any> = createAction<SetVerificationIsLimitReachedPayload>(SetVerificationIsLimitReachedType);
